import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import Checkbox from '@material-ui/core/Checkbox';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import OutlinedInput from '@material-ui/core/OutlinedInput';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';
import GroupSelect from 'app/components/group/GroupSelect';
import RedButton from 'app/components/buttons/RedButton';

import BillingPeriods from 'app/utils/BillingPeriods';
import AccessOptions from 'app/utils/AccessOptions';
import HandleResponse from 'app/utils/HandleResponse';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    accessOptionsLoading: {
        height: '100px',
        position: 'relative'
    },
    pointer: {
        cursor: 'pointer'
    },
    flex: {
        display: 'flex'
    },
    groupSelect: {
        backgroundColor: theme.palette.grey[100],
        marginBottom: theme.spacing(2),
        borderRadius: theme.shape.borderRadius
    },
    emptyCon: {
        textAlign: 'center',
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    margin: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    emailInput: {
        textAlign: 'right',
    },
}));

const Months = {
    'January': 31,
    'February': 28,
    'March': 31,
    'April': 30,
    'May': 31,
    'June': 30,
    'July': 31,
    'August': 31,
    'September': 30,
    'October': 31,
    'November': 30,
    'December': 31
};

const EmailAccessLevels = {
    'Read Only': 'readonly',
    'Members': 'members',
    'Global': 'everyone'
};

const Days = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28, 29,30,31];

const DaySelect = props => {

    const { label, month, day, handleChange } = props;

    let options = [];

    for (let i = 1; i <= Months[month]; i++) {
        options.push(<MenuItem key={i} value={i}>{i}</MenuItem>)
    }

    return (
        <TextField
            fullWidth
            select
            label={label}
            value={day}
            onChange={handleChange}
            variant="outlined"
            >
            {options}
        </TextField>
    );
};

const initialValues = {
    id: 0,
    parent_id: 0,
    title: '',
    member_called: '',
    description: '',
    email: '',
    email_access_level: 'readonly',
    access: [],
    billing_period: 'inherit',
    fee: 0.00,
    late_fee: 0.00,
    due_by_month: 'January',
    due_by_day: 1,
    late_by_month: 'January',
    late_by_day: 1,
    bi_due_by_month: 'January',
    bi_due_by_day: 1,
    bi_late_by_month: 'January',
    bi_late_by_day: 1,
    public: false,
};

export default function EditGroupDialog(props) {

    const { variant, open, group, onClose, onSave, onDelete, onClosed, singular } = props;

    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();

    const [ values, setValues ] = useState(initialValues);

    const [ viewAdvancedAccessOptions, setViewAdvancedAccessOptions ] = useState(false);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    useEffect(() => {

        if (group) setValues(HandleResponse(group, initialValues));
        setErrors({});

    }, [group]);

    const handleChange = name => event => {

        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        if (name == 'due_by_month' && values.due_by_day > 28) {
            setValues({...values, due_by_day: 1, due_by_month: value })
        } else {
            setValues({ ...values, [name]: value });
        }
    };

    const handleParentId = id => {
        setValues({...values, parent_id: id })
    }

    const handleAdminAccess = () => {
        if (AccessOptions.length === values.access.length) {
            setValues({...values, access: []});
        } else {
            setValues({...values, access: AccessOptions});
        }
    }

    const handleAccess = option => {

        const access = [...values.access];
        const i = access.indexOf(option)

        if (i > -1) {
            access.splice(i, 1);
        } else {
            access.push(option);
        }

        setValues({ ...values, access: access });
    }

    const toggleAdvancedAccessOptions = () => {
        setViewAdvancedAccessOptions(!viewAdvancedAccessOptions);
    }

    const postGroup = () => {

        setLoading(true);

        const data = {...values};

        api.post('/admin/group/' + variant + '/' + values.id, data)
            .then(() => onSave())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    };

    const deleteGroup = () => {
        setLoading(true);

        api.delete('/admin/group/' + variant + '/' + values.id)
            .then(() => onDelete())
            .finally(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const getEmailMessage = () => {
        if (isError('email')) return errors.email;
        return "Email address used to send email to members of this group.";
    }

    const getEmailAccessLevelMessage = () => {
        if (isError('email_access_level')) return errors.email_access_level;
        return "The Read Only option will only allow emails from the app to the group. The Members option will allow users in the group to email the group as a whole. The Global option will allow group users to receive email from any email address.";
    }

    const clearState = () => {
        setValues(initialValues);
    };

    const onExited = () => {
        clearState();
        onClosed();
    }

    // Access group

    const accessGroup = () => {
        if (viewAdvancedAccessOptions) {
            return (
                <FormGroup>
                    {AccessOptions.map(option => (
                        <FormControlLabel
                            key={option}
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={values.access.indexOf(option) > -1}
                                    onChange={() => handleAccess(option)}
                                    value={option}
                                />
                            }
                            label={option}
                        />
                    ))}
                </FormGroup>
            )
        } else {
            return (
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                checked={(AccessOptions.length === values.access.length)}
                                onChange={handleAdminAccess}
                                value={'admin'}
                            />
                        }
                        label={'Full Administrative Access'}
                    />
                </FormGroup>
            )
        }
    };

    const billingForm = () => {
        switch (values.billing_period) {
            case 'monthly':
                return (
                    <div className={classes.margin}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Fee"
                                    variant="outlined"
                                    inputProps={{
                                        type: 'number',
                                    }}
                                    value={values.fee}
                                    onChange={handleChange('fee')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Invoice Day"
                                    value={values.due_by_day}
                                    onChange={handleChange('due_by_day')}
                                    variant="outlined"
                                    >
                                    {Days.map( day => (
                                        <MenuItem key={day} value={day}>
                                            {day}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Late Fee"
                                    variant="outlined"
                                    inputProps={{
                                        type: 'number',
                                    }}
                                    value={values.late_fee}
                                    onChange={handleChange('late_fee')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Late By Day"
                                    value={values.late_by_day}
                                    onChange={handleChange('late_by_day')}
                                    variant="outlined"
                                    >
                                    {Days.map( day => (
                                        <MenuItem key={day} value={day}>
                                            {day}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </div>
                )

            case  'annual':
                return (
                    <div className={classes.margin}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Fee"
                                    variant="outlined"
                                    inputProps={{
                                        type: 'number',
                                    }}
                                    value={values.fee}
                                    onChange={handleChange('fee')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Invoice Month"
                                    value={values.due_by_month}
                                    onChange={handleChange('due_by_month')}
                                    variant="outlined"
                                    >
                                    {Object.keys(Months).map( month => (
                                        <MenuItem key={month} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <DaySelect
                                    label="Invoice Day"
                                    month={values.due_by_month}
                                    day={values.due_by_day}
                                    handleChange={handleChange('due_by_day')}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Late Fee"
                                    variant="outlined"
                                    inputProps={{
                                        type: 'number',
                                    }}
                                    value={values.late_fee}
                                    onChange={handleChange('late_fee')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Late By Month"
                                    value={values.late_by_month}
                                    onChange={handleChange('late_by_month')}
                                    variant="outlined"
                                    >
                                    {Object.keys(Months).map( month => (
                                        <MenuItem key={month} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <DaySelect
                                    label="Late By Day"
                                    month={values.late_by_month}
                                    day={values.late_by_day}
                                    handleChange={handleChange('late_by_day')}
                                />
                            </Grid>
                        </Grid>
                    </div>
                )

            case 'bi-annual':
                return (
                    <div className={classes.margin}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Fee"
                                    variant="outlined"
                                    inputProps={{
                                        type: 'number',
                                    }}
                                    value={values.fee}
                                    onChange={handleChange('fee')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Late Fee"
                                    variant="outlined"
                                    inputProps={{
                                        type: 'number',
                                    }}
                                    value={values.late_fee}
                                    onChange={handleChange('late_fee')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="First Invoice Month"
                                    value={values.due_by_month}
                                    onChange={handleChange('due_by_month')}
                                    variant="outlined"
                                    >
                                    {Object.keys(Months).map( month => (
                                        <MenuItem key={month} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <DaySelect
                                    label="First Invoice Day"
                                    month={values.due_by_month}
                                    day={values.due_by_day}
                                    handleChange={handleChange('due_by_day')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="First Late By Month"
                                    value={values.late_by_month}
                                    onChange={handleChange('late_by_month')}
                                    variant="outlined"
                                    >
                                    {Object.keys(Months).map( month => (
                                        <MenuItem key={month} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <DaySelect
                                    label="First Late By Day"
                                    month={values.late_by_month}
                                    day={values.late_by_day}
                                    handleChange={handleChange('late_by_day')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Second Invoice Month"
                                    value={values.bi_due_by_month}
                                    onChange={handleChange('bi_due_by_month')}
                                    variant="outlined"
                                    >
                                    {Object.keys(Months).map( month => (
                                        <MenuItem key={month} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <DaySelect
                                    label="Second Invoice Day"
                                    month={values.bi_due_by_month}
                                    day={values.bi_due_by_day}
                                    handleChange={handleChange('bi_due_by_day')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Second Late By Month"
                                    value={values.bi_late_by_month}
                                    onChange={handleChange('bi_late_by_month')}
                                    variant="outlined"
                                    >
                                    {Object.keys(Months).map( month => (
                                        <MenuItem key={month} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <DaySelect
                                    label="Second Late By Day"
                                    month={values.bi_late_by_month}
                                    day={values.bi_late_by_day}
                                    handleChange={handleChange('bi_late_by_day')}
                                />
                            </Grid>
                        </Grid>
                    </div>
                )
            case 'none':
            case 'inherit':
            default:
                return (<div className={classes.marginTop}></div>);
        }
    };

    const billingHelperText = useMemo(() => {
        switch (values.billing_period) {
            case 'inherit':
                return "Users in this level will inherit their billing from the parent level."

            case 'none':
                return "Users in this level will not be charged unless in a sub-level that has billing enabled."

            case 'monthly':
                return "Enter you monthly Fee, invoice day and optional Late Fee / Late By Day. The Late Fee should be the entire amount (i.e. if your Fee is $35.00 and you have a $5.00 late penalty, the Late Fee should be $40.00). If a day doesn't exist for any given month, we will use the last day of the month that is available."

            case  'annual':
                return "Be sure to use the entire amount for the Late Fee (i.e. if your Fee is $35.00 and you have a $5.00 late penalty, the Late Fee should be $40.00)."

            case 'bi-annual':
                return "Be sure to use the entire amount for the Late Fee (i.e. if your Fee is $35.00 and you have a $5.00 late penalty, the Late Fee should be $40.00)."
        }
    }, [values.billing_period]);

    const memberCalledHelperText = () => {
        if (isError('member_called')) return getErrorMessage('member_called');
        return 'How should we reference an individual user that is a member of this group?'
    }

    const emailDomain = useMemo(() => {


        if (! app.club.meta.mail_domain) {
            const regex = /(?:http[s]*\:\/\/)*(.*?)\.(?=[^\/]*\..{2,5})/i

            const result = app.club.url.match(regex);

            return '.' + result[1] + '@mx.clubapps.org';
        }

        return '@' + app.club.meta.mail_domain;
    }, [app.club])

    return (
        <Dialog
            aria-labelledby="edit-group-dialog-title"
            open={open}
            aria-describedby="edit-group-dialogue-description"
            fullWidth
            maxWidth="sm"
            onExited={onExited}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-group-dialog-title">
                <span>{group ? "Edit " + values.title : "New " + singular}</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <form>
                    {(!loading && values.parent_id === 0 && variant === 'level') &&
                        <Typography variant="subtitle2" color="secondary" align="center" gutterBottom>
                            {values.members_level ? "Base Member Level" : "Base Guest Level"}
                        </Typography>
                    }
                    <TextField
                        id="group-title"
                        label="Title"
                        variant="outlined"
                        fullWidth
                        value={values.title}
                        onChange={handleChange('title')}
                        margin="normal"
                        required
                    />
                    <TextField
                        id="group-member-called"
                        label="User Referenced As"
                        variant="outlined"
                        fullWidth
                        value={values.member_called}
                        onChange={handleChange('member_called')}
                        margin="normal"
                        helperText={memberCalledHelperText()}
                    />
                    <TextField
                        id="group-description"
                        label="Description"
                        multiline
                        rows="5"
                        variant="outlined"
                        fullWidth
                        value={values.description}
                        onChange={handleChange('description')}
                        margin="normal"
                    />
                    <FormControl variant="outlined" error={isError('email')} fullWidth className={classes.marginTop}>
                        <OutlinedInput
                            id="input-email"
                            value={values.email}
                            onChange={handleChange('email')}
                            endAdornment={<InputAdornment>{emailDomain}</InputAdornment>}
                            aria-describedby="email-helper-text"
                            labelWidth={0}
                            classes={{ input: classes.emailInput }}
                            placeholder={(values.title && values.title != '') ? values.title.replace(/ /g,".").toLowerCase() : "mygroup"}
                        />
                        <FormHelperText id="email-helper-text">{getEmailMessage()}</FormHelperText>
                    </FormControl>
                    <TextField
                        id="select-email-access-level"
                        fullWidth
                        select
                        label="Email Access Level"
                        value={values.email_access_level}
                        onChange={handleChange('email_access_level')}
                        margin="normal"
                        variant="outlined"
                        helperText={getEmailAccessLevelMessage()}
                        >
                        {Object.entries(EmailAccessLevels).map(([label, value]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {variant == 'level' &&
                        <div className={classes.marginTop}>
                            { !(values.id > 0 && values.parent_id == 0) &&
                                <div className={classes.groupSelect}>
                                    <GroupSelect
                                        initGroupId={values.parent_id}
                                        unselectableId={values.id}
                                        variant="level"
                                        label="Select a Parent Level"
                                        onSelect={handleParentId}
                                        />
                                </div>
                            }
                            <FormControl
                                component="fieldset"
                                margin="normal"
                                error={isError('access')}
                                >
                                <FormLabel component="legend">
                                    Access Options &nbsp;&nbsp;&nbsp;&nbsp;
                                    <Link className={classes.pointer} onClick={toggleAdvancedAccessOptions}>
                                        {viewAdvancedAccessOptions ? 'Hide Advanced' : 'View Advanced'}
                                    </Link>
                                </FormLabel>
                                {accessGroup()}
                                <FormHelperText>{getErrorMessage('access')}</FormHelperText>
                            </FormControl>
                            <TextField
                                id="select-billing-period"
                                fullWidth
                                select
                                label="Billing Period"
                                value={values.billing_period}
                                onChange={handleChange('billing_period')}
                                margin="normal"
                                variant="outlined"
                                helperText={billingHelperText}
                                >
                                {BillingPeriods.map( period => (
                                    <MenuItem key={period} value={period}>
                                        {window.ucFirst(period)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {billingForm()}
                        </div>
                    }
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.public}
                                onChange={handleChange('public')}
                                value="public"
                                color="primary"
                            />
                        }
                        label={"Allow users to see " + variant + "?"}
                        margin="normal"
                    />
                </form>
            </DialogContent>
            <DialogActions>
                { (group && ! (group.parent_id === 0 && group.membership_level == true)) &&
                    <RedButton onClick={deleteGroup}>
                        Delete
                    </RedButton>
                }
                <Button onClick={postGroup} color="primary" variant="contained">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
